import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  generateImportCode,
  ImportCodeError,
  ImportCodeResponse,
} from '../api/importCode';
import ImportCodeModal from './ImportCodeModal';

interface ImportCodeButtonProps {
  projectId: string;
  hasScript: boolean;
  menuMode?: boolean;
  onActionStart?: () => void;
  onImported?: () => void;
}

function useDescribeError() {
  const { t } = useTranslation('common');
  return (err: unknown): string => {
    if (err instanceof ImportCodeError) {
      switch (err.code) {
        case 'script_already_exists':
          return t('importCode.scriptAlreadyExists');
        case 'project_not_found':
          return t('importCode.projectNotFound');
        case 'invalid_request':
          return t('importCode.invalidRequest');
        case 'internal_error':
          return t('importCode.internalError');
        default:
          return err.message || t('generateImportCodeFailed');
      }
    }
    if (err instanceof Error) {
      return err.message || t('generateImportCodeFailed');
    }
    return t('generateImportCodeFailed');
  };
}

const ImportCodeButton: React.FC<ImportCodeButtonProps> = ({
  projectId,
  hasScript,
  menuMode = false,
  onActionStart,
  onImported,
}) => {
  const { t } = useTranslation('common');
  const describeError = useDescribeError();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [response, setResponse] = useState<ImportCodeResponse | null>(null);

  if (hasScript) {
    return null;
  }

  const handleClick = async () => {
    onActionStart?.();
    setLoading(true);
    setErrorMsg('');
    try {
      const result = await generateImportCode(projectId);
      setResponse(result);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ImportCodeButton] generate import code failed', err);
      setErrorMsg(describeError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResponse(null);
  };

  if (menuMode) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          disabled={loading}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.5rem 0.85rem',
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            fontSize: '0.875rem',
            color: 'var(--text-1)',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--primary-light)';
            }
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          }}
        >
          {loading ? t('generating') : t('importCode.importFromScript')}
        </button>
        {errorMsg && (
          <p
            className="error-text"
            style={{ margin: '0.25rem 0.85rem 0.5rem', fontSize: '0.8rem' }}
          >
            {errorMsg}
          </p>
        )}
        {response && (
          <ImportCodeModal
            code={response.code}
            expiresAt={response.expiresAt}
            onClose={handleClose}
            onImported={onImported}
          />
        )}
      </>
    );
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleClick}
        disabled={loading}
      >
        {loading ? t('generating') : t('importCode.importFromScript')}
      </button>
      {errorMsg && (
        <p
          className="error-text"
          style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem' }}
        >
          {errorMsg}
        </p>
      )}
      {response && (
        <ImportCodeModal
          code={response.code}
          expiresAt={response.expiresAt}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default ImportCodeButton;
